import { formatParisDateTime, type BundleListItem } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FlaskConical, Plus, Upload } from 'lucide-react';
import { useState, type JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchBundlesList, type BundleListParams } from './api.js';
import { readBundleFilters } from './filters.js';
import { BundleImportDialog } from './bundle-import-dialog.js';
import { BundleTestSheet } from './bundle-test-sheet.js';

const SORTABLE_COLUMNS = ['sku', 'enabled', 'lastUsedAt', 'updatedAt'] as const;
const NUMERIC_COLUMNS = ['componentCount'] as const;

const ALL_VALUE = '__all__';

const helper = createDataTableColumnHelper<BundleListItem>();

const columns = helper.columns([
  helper.accessor('sku', {
    header: 'Bundle',
    // Le libellé, quand il existe, remplace le SKU ; le SKU reste lisible
    // dessous puisque c'est lui qu'on retrouve dans Shopify.
    cell: (info) => {
      const { label } = info.row.original;
      return label === null ? (
        <span className="whitespace-nowrap">{info.getValue()}</span>
      ) : (
        <span className="flex flex-col">
          <span>{label}</span>
          <span className="whitespace-nowrap text-xs text-muted-foreground">{info.getValue()}</span>
        </span>
      );
    },
  }),
  helper.accessor('componentCount', { header: 'Composants' }),
  helper.accessor('enabled', {
    header: 'Statut',
    cell: (info) => (
      <Badge tone={info.getValue() ? 'green' : 'gray'}>
        {info.getValue() ? 'Actif' : 'Inactif'}
      </Badge>
    ),
  }),
  helper.accessor('lastUsedAt', {
    header: 'Dernière commande',
    cell: (info) => {
      const value = info.getValue();
      return value === null ? (
        <span className="text-muted-foreground">Jamais</span>
      ) : (
        <span className="whitespace-nowrap tabular-nums">
          {formatParisDateTime(new Date(value))}
        </span>
      );
    },
  }),
  helper.accessor('updatedAt', {
    header: 'Modifié le',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
]);

/**
 * Liste des bundles (`02` §8), refondue le 10/09/2026 : la ligne mène à une
 * **page** dédiée (`/bundles/$sku`), plus à un dialogue. « Dernière commande »
 * répond à la question qu'on se pose devant une liste qui grossit — ce bundle
 * sert-il encore ?
 *
 * Plus de colonne « surcharges par boutique » : un bundle a une seule
 * composition.
 */
export function BundlesPage(): JSX.Element {
  const rawSearch = useSearch({ strict: false });
  const navigate = useNavigate();
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const filters = readBundleFilters(rawSearch);

  const [importOpen, setImportOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const listParams: BundleListParams = {
    page,
    pageSize: 50,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.enabled !== undefined ? { enabled: filters.enabled } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.bundles.list({ ...listParams }),
    queryFn: () => fetchBundlesList(listParams),
  });

  const isFiltered = filters.search !== undefined || filters.enabled !== undefined;

  return (
    <Page>
      <PageHeader
        title="Bundles"
        description="Un bundle est un SKU vendu sur Shopify qui se décompose en plusieurs produits chez Martelet."
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setTestOpen(true);
              }}
            >
              <FlaskConical aria-hidden="true" />
              Tester un SKU
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setImportOpen(true);
              }}
            >
              <Upload aria-hidden="true" />
              Importer un CSV
            </Button>
            <Button
              type="button"
              onClick={() => {
                void navigate({ to: '/bundles/nouveau' });
              }}
            >
              <Plus aria-hidden="true" />
              Créer un bundle
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={50}
        sortBy={sortBy}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(row) => {
          void navigate({ to: '/bundles/$sku', params: { sku: row.sku } });
        }}
        isLoading={query.isPending}
        isError={query.isError}
        {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={
          <ListToolbar
            search={{
              value: filters.search ?? '',
              onChange: (next) => {
                setFilters({ search: next === '' ? undefined : next });
              },
              placeholder: 'SKU, libellé ou SKU d’un composant…',
              label: 'Rechercher un bundle',
            }}
            isFiltered={isFiltered}
            onReset={() => {
              setFilters({ search: undefined, enabled: undefined });
            }}
          >
            <Select
              value={filters.enabled === undefined ? ALL_VALUE : filters.enabled ? 'true' : 'false'}
              onValueChange={(next) => {
                setFilters({ enabled: next === ALL_VALUE ? undefined : next === 'true' });
              }}
            >
              <SelectTrigger className="w-40" aria-label="Statut">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Tous les statuts</SelectItem>
                <SelectItem value="true">Actifs</SelectItem>
                <SelectItem value="false">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </ListToolbar>
        }
        emptyState={{
          title: 'Aucun bundle',
          description: 'Créez-en un ou importez un CSV.',
          action: (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setImportOpen(true);
                }}
              >
                Importer un CSV
              </Button>
              <Button
                type="button"
                onClick={() => {
                  void navigate({ to: '/bundles/nouveau' });
                }}
              >
                Créer un bundle
              </Button>
            </>
          ),
        }}
      />

      <BundleImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          void query.refetch();
        }}
      />

      <BundleTestSheet open={testOpen} onOpenChange={setTestOpen} />
    </Page>
  );
}
